import { DriverCounts, DriverRecord, DriversStateModel, OnlineUserRecord } from '../core/state/drivers.store';
import {
  OrderRecord,
  OrdersStateModel,
  OrderStopRecord,
  OrderTrackingState
} from '../core/state/orders.store';
import { OptionRecord, ReferenceDataStateModel, RegionRecord, TariffRecord } from '../core/state/reference-data.store';
import { DEFAULT_CANCEL_LINGER_MS } from '../core/state/cancel-linger';
import { localPhoneForms } from '../shared-ui/phone-search';

type UnknownRecord = Record<string, unknown>;

export function buildOrdersSnapshot(
  rawOrders: unknown[],
  currentState: OrdersStateModel
): OrdersStateModel {
  const nextById: Record<string, OrderRecord> = {};
  const pendingIds: string[] = [];
  const activeIds: string[] = [];
  const allIds: string[] = [];

  for (const rawOrder of rawOrders) {
    const order = normalizeOrderRecord(rawOrder);

    if (!order.orderId) {
      continue;
    }

    // The orders list carries no `processing` flag (it only arrives via the processingStarted
    // socket event), so a refresh must not wipe it while the order is still ringing a driver.
    if (!hasOwnKey(rawOrder, 'processing') && order.statusId === 1 && !order.cancelDate) {
      order.processing = currentState.byId[order.orderId]?.processing ?? false;
    }

    nextById[order.orderId] = applyTrackingState(order, currentState.tracking);
    allIds.push(order.orderId);

    if (order.statusId === 1) {
      pendingIds.push(order.orderId);
    } else {
      activeIds.push(order.orderId);
    }
  }

  return {
    ...currentState,
    byId: nextById,
    pendingIds,
    activeIds,
    allIds,
    deliveryIds: [],
    balanceIds: [],
    updatedAt: new Date().toISOString()
  };
}

function hasOwnKey(value: unknown, key: string): boolean {
  return typeof value === 'object' && value !== null && key in value;
}

export function applyNewOrderEvent(
  currentState: OrdersStateModel,
  rawPayload: unknown
): OrdersStateModel {
  const newOrder = normalizeOrderRecord(parseSocketPayload(rawPayload));

  if (!newOrder.orderId) {
    return currentState;
  }

  const nextState: OrdersStateModel = {
    ...currentState,
    byId: {
      ...currentState.byId,
      [newOrder.orderId]: applyTrackingState(newOrder, currentState.tracking)
    },
    allIds: currentState.allIds.includes(newOrder.orderId)
      ? currentState.allIds
      : [...currentState.allIds, newOrder.orderId],
    pendingIds:
      newOrder.statusId === 1
        ? appendUnique(currentState.pendingIds, newOrder.orderId)
        : currentState.pendingIds,
    activeIds:
      newOrder.statusId !== 1
        ? appendUnique(currentState.activeIds, newOrder.orderId)
        : currentState.activeIds,
    updatedAt: new Date().toISOString()
  };

  return markDuplicateOrders(nextState, newOrder.userId);
}

export function applyUpdatedOrderEvent(
  currentState: OrdersStateModel,
  rawPayload: unknown
): OrdersStateModel {
  const parsedPayload = parseSocketPayload(rawPayload);
  const payloadRecord = asRecord(parsedPayload);
  const rawOrderPayload =
    payloadRecord['order'] !== undefined ? parseSocketPayload(payloadRecord['order']) : parsedPayload;
  const rawOrderRecord = asRecord(rawOrderPayload);
  const updatedOrder = normalizeOrderRecord(rawOrderPayload);

  if (!updatedOrder.orderId || !currentState.byId[updatedOrder.orderId]) {
    return currentState;
  }

  const orderId = updatedOrder.orderId;
  const existingOrder = currentState.byId[orderId];

  if (!('processing' in rawOrderRecord) && updatedOrder.statusId === 1 && !updatedOrder.cancelDate) {
    updatedOrder.processing = existingOrder.processing;
  }
  if (!('is_corporate' in rawOrderRecord)) {
    updatedOrder.corporate = existingOrder.corporate;
  }
  if (!('is_business' in rawOrderRecord)) {
    updatedOrder.business = existingOrder.business;
  }
  if (
    updatedOrder.destinationLatitude === null &&
    updatedOrder.destinationLongitude === null &&
    (existingOrder.destinationLatitude !== null || existingOrder.destinationLongitude !== null)
  ) {
    updatedOrder.destinationLatitude = existingOrder.destinationLatitude;
    updatedOrder.destinationLongitude = existingOrder.destinationLongitude;
    updatedOrder.destinationAddress = existingOrder.destinationAddress || updatedOrder.destinationAddress;
    updatedOrder.destinationComment = existingOrder.destinationComment || updatedOrder.destinationComment;
  }
  const wasPending = currentState.pendingIds.includes(orderId);
  const wasActive = currentState.activeIds.includes(orderId);
  const pendingIds = currentState.pendingIds.filter((id) => id !== orderId);
  const activeIds = currentState.activeIds.filter((id) => id !== orderId);
  const allIds = currentState.allIds.filter((id) => id !== orderId);
  const nextById = { ...currentState.byId };

  if (updatedOrder.completedDate) {
    delete nextById[orderId];

    return {
      ...currentState,
      byId: nextById,
      pendingIds,
      activeIds,
      allIds,
      updatedAt: new Date().toISOString()
    };
  }

  const nextOrder = applyTrackingState(updatedOrder, currentState.tracking);

  if (wasPending) {
    nextById[orderId] = nextOrder;
    // Match the periodic full-refresh's bucketing rule (statusId alone) — requiring
    // acceptedDate too created a race: an update event can report the new statusId before
    // its acceptedDate (a separate orderDetails row) is populated, leaving an already-accepted
    // order stuck in Pending until the next poll corrects it.
    const shouldMoveToActive = updatedOrder.statusId !== 1;

    return {
      ...currentState,
      byId: nextById,
      pendingIds: shouldMoveToActive ? pendingIds : appendUnique(pendingIds, orderId),
      activeIds: shouldMoveToActive ? appendUnique(activeIds, orderId) : activeIds,
      allIds: appendUnique(allIds, orderId),
      updatedAt: new Date().toISOString()
    };
  }

  if (wasActive && updatedOrder.cancelDate) {
    const pendingOrder =
      updatedOrder.statusId === 1
        ? { ...nextOrder, cancelDate: null, acceptedDate: null }
        : nextOrder;

    nextById[orderId] = pendingOrder;
    return {
      ...currentState,
      byId: nextById,
      pendingIds: appendUnique(pendingIds, orderId),
      activeIds,
      allIds: appendUnique(allIds, orderId),
      updatedAt: new Date().toISOString()
    };
  }

  if (wasActive && updatedOrder.statusId === 1) {
    nextById[orderId] = nextOrder;
    return {
      ...currentState,
      byId: nextById,
      pendingIds: appendUnique(pendingIds, orderId),
      activeIds,
      allIds: appendUnique(allIds, orderId),
      updatedAt: new Date().toISOString()
    };
  }

  if (wasActive) {
    nextById[orderId] = nextOrder;
    return {
      ...currentState,
      byId: nextById,
      pendingIds,
      activeIds: appendUnique(activeIds, orderId),
      allIds: appendUnique(allIds, orderId),
      updatedAt: new Date().toISOString()
    };
  }

  nextById[orderId] = nextOrder;

  return {
    ...currentState,
    byId: nextById,
    // Same statusId-only rule as above and as the periodic full refresh — see comment there.
    pendingIds:
      updatedOrder.statusId === 1 ? appendUnique(pendingIds, orderId) : pendingIds,
    activeIds:
      updatedOrder.statusId !== 1 ? appendUnique(activeIds, orderId) : activeIds,
    allIds: appendUnique(allIds, orderId),
    updatedAt: new Date().toISOString()
  };
}

export function applyProcessingStarted(
  currentState: OrdersStateModel,
  orderId: string
): OrdersStateModel {
  if (!currentState.pendingIds.includes(orderId) || !currentState.byId[orderId]) {
    return currentState;
  }

  return {
    ...currentState,
    byId: {
      ...currentState.byId,
      [orderId]: {
        ...currentState.byId[orderId],
        processing: true
      }
    },
    updatedAt: new Date().toISOString()
  };
}

export function applyProcessingFinished(
  currentState: OrdersStateModel,
  orderId: string
): OrdersStateModel {
  if (!currentState.pendingIds.includes(orderId) || !currentState.byId[orderId]) {
    return currentState;
  }

  return {
    ...currentState,
    byId: {
      ...currentState.byId,
      [orderId]: {
        ...currentState.byId[orderId],
        processing: false,
        driverFirstName: null,
        driverLastName: null,
        driverName: '',
        driverId: null,
        driverMobile: null,
        carManufacturer: null,
        carNumber: null,
        carColor: null
      }
    },
    updatedAt: new Date().toISOString()
  };
}

export function removeCancelledPendingOrders(
  currentState: OrdersStateModel,
  lingerMsFor: (order: OrderRecord) => number = () => DEFAULT_CANCEL_LINGER_MS
): OrdersStateModel {
  const now = Date.now();
  const toRemove = new Set(
    currentState.pendingIds.filter((orderId) => {
      const order = currentState.byId[orderId];
      if (!order?.cancelDate) return false;
      return now - new Date(order.cancelDate).getTime() >= lingerMsFor(order);
    })
  );

  if (toRemove.size === 0) return currentState;

  const nextById = { ...currentState.byId };
  for (const id of toRemove) delete nextById[id];

  return {
    ...currentState,
    byId: nextById,
    pendingIds: currentState.pendingIds.filter((id) => !toRemove.has(id)),
    allIds: currentState.allIds.filter((id) => !toRemove.has(id)),
    updatedAt: new Date().toISOString()
  };
}

export function normalizeRegions(rawRegions: unknown[]): RegionRecord[] {
  return rawRegions
    .map((rawRegion) => {
      const region = asRecord(rawRegion);
      return {
        id: readNumber(region, 'regionId') ?? readNumber(region, 'id') ?? -1,
        name: readString(region, 'regionName') ?? readString(region, 'name') ?? 'Unknown region',
        childIds: readArray(region, 'childs')
          .map((childId) => readNumber({ childId }, 'childId'))
          .filter(isNumber),
        coordinates: normalizeCoordinates(readArray(region, 'coordinates')),
        state: readString(region, 'state')
      };
    })
    .filter((region) => region.id >= 0);
}

const LEGACY_TARIFF_NAME_OVERRIDES: Record<string, string> = {
  ggASAP: 'ggTurbo'
};

function applyLegacyTariffNameOverride(name: string): string {
  return LEGACY_TARIFF_NAME_OVERRIDES[name] ?? name;
}

export function normalizeTariffs(rawTariffs: unknown[]): TariffRecord[] {
  return rawTariffs
    .map((rawTariff) => {
      const tariff = asRecord(rawTariff);
      const details = readJsonRecord(tariff['details']);
      const description = readJsonRecord(tariff['description']);
      return {
        id: readNumber(tariff, 'tariffId') ?? readNumber(tariff, 'id') ?? -1,
        name: applyLegacyTariffNameOverride(
          readString(tariff, 'tariffname') ??
            readString(tariff, 'name') ??
            readString(details, 'name') ??
            readString(description, 'en') ??
            readString(description, 'hy') ??
            'Unknown tariff'
        ),
        typeId: readNumber(tariff, 'typeId'),
        regionId: readNumber(tariff, 'regionId'),
        ...(readString(tariff, 'typeName') ? { typeName: readString(tariff, 'typeName') } : {})
      };
    })
    .filter((tariff) => tariff.id >= 0);
}

export function normalizeOptions(rawOptions: unknown[]): OptionRecord[] {
  const options = rawOptions
    .map((rawOption) => {
      const option = asRecord(rawOption);
      return {
        id: readNumber(option, 'optionId') ?? readNumber(option, 'id') ?? -1,
        name: readString(option, 'optionName') ?? readString(option, 'name') ?? 'Unknown option'
      };
    })
    .filter((option) => option.id >= 0);

  if (!options.some((option) => option.id === 0)) {
    options.push({
      id: 0,
      name: 'All'
    });
  }

  return options;
}

export function applyReferenceDataUpdate(
  currentState: ReferenceDataStateModel,
  payload: Partial<ReferenceDataStateModel>
): ReferenceDataStateModel {
  return {
    ...currentState,
    ...payload,
    loadedAt: new Date().toISOString()
  };
}

export function buildDriversSnapshot(
  rawPartners: unknown[],
  currentState: DriversStateModel
): DriversStateModel {
  return finishDriversSnapshot(rawPartners.map((rawPartner) => normalizeDriverRecord(rawPartner)), currentState);
}

/**
 * Slow-changing driver data (identity, car, tariffs, options, payment methods) from GET /partners/details,
 * normalized once and kept between the frequent live refreshes.
 */
export function buildDriverDetails(rawDetails: unknown[]): Map<string, DriverRecord> {
  const details = new Map<string, DriverRecord>();

  for (const rawDetail of rawDetails) {
    const driver = normalizeDriverRecord(rawDetail);

    if (driver.userId) {
      details.set(driver.userId, driver);
    }
  }

  return details;
}

/**
 * Builds the drivers snapshot from the fast-changing GET /partners/live rows plus the cached details.
 * Same result as buildDriversSnapshot over the combined GET /partners rows. A live row without details yet
 * (a driver that just came online) is skipped and counted, so the caller can refresh the details.
 */
export function buildDriversSnapshotFromLive(
  rawLive: unknown[],
  details: ReadonlyMap<string, DriverRecord>,
  currentState: DriversStateModel
): { state: DriversStateModel; missingDetailsCount: number } {
  const drivers: DriverRecord[] = [];
  let missingDetailsCount = 0;

  for (const rawRow of rawLive) {
    const liveDriver = normalizeDriverRecord(rawRow);
    const base = details.get(liveDriver.userId);

    if (!base) {
      missingDetailsCount += 1;
      continue;
    }

    drivers.push(mergeDriverRecord(base, liveDriver));
  }

  return { state: finishDriversSnapshot(drivers, currentState), missingDetailsCount };
}

function finishDriversSnapshot(drivers: DriverRecord[], currentState: DriversStateModel): DriversStateModel {
  const byId: Record<string, DriverRecord> = {};
  const ids: string[] = [];

  for (const driver of drivers) {
    if (!driver.userId) {
      continue;
    }

    ids.push(driver.userId);
    byId[driver.userId] = currentState.byId[driver.userId]
      ? mergeDriverRecord(currentState.byId[driver.userId], driver)
      : driver;
  }

  // During peak hours the backend sometimes returns a partial list under load.
  // If the response drops >15% of the current driver count (and there are enough
  // drivers to make this meaningful), preserve the existing drivers that are
  // missing from the response rather than wiping them off the map. They will be
  // removed naturally on the next healthy poll that returns a full list.
  const currentCount = currentState.ids.length;
  const droppedFraction = currentCount > 0 ? (currentCount - ids.length) / currentCount : 0;

  if (droppedFraction > 0.15 && currentCount > 20) {
    for (const existingId of currentState.ids) {
      if (!byId[existingId]) {
        byId[existingId] = currentState.byId[existingId];
        ids.push(existingId);
      }
    }
  }

  return {
    ...currentState,
    byId,
    ids,
    updatedAt: new Date().toISOString()
  };
}

export function applyOnlineUsersUpdate(
  currentState: DriversStateModel,
  rawUsers: unknown[]
): DriversStateModel {
  return {
    ...currentState,
    onlineUsers: rawUsers.map((rawUser, index) => normalizeOnlineUserRecord(rawUser, index)),
    updatedAt: new Date().toISOString()
  };
}

export function applyPartnerCounts(
  currentState: DriversStateModel,
  rawCounts: unknown[]
): DriversStateModel {
  const countByStatus = new Map<number, number>();

  for (const entry of rawCounts) {
    const record = asRecord(entry);
    const status = readNumber(record, 'status');

    if (status !== null) {
      countByStatus.set(status, readNumber(record, 'count') ?? 0);
    }
  }

  // live.statuses: 0 = Ready, 1 = Busy, 2 = Attaching. Ready and Attaching both count as "free".
  const free = (countByStatus.get(0) ?? 0) + (countByStatus.get(2) ?? 0);
  const busy = countByStatus.get(1) ?? 0;

  return {
    ...currentState,
    counts: {
      free,
      busy,
      offline: currentState.counts.offline
    },
    updatedAt: new Date().toISOString()
  };
}

export function applyDriverLocationUpdates(
  currentState: DriversStateModel,
  rawLocations: unknown[]
): DriversStateModel {
  const nextById = { ...currentState.byId };

  for (const rawLocation of rawLocations) {
    const location = asRecord(rawLocation);
    const driverKey = readIdentifier(location, 'partnerId') ?? readIdentifier(location, 'userId') ?? '';

    if (!driverKey) {
      continue;
    }

    const coordinates = parseLocationCoordinates(location['location']);
    const currentDriver = nextById[driverKey];
    const locationDriver = normalizeDriverRecord(rawLocation);
    const towards = asRecord(location['towards']);

    nextById[driverKey] = {
      userId: driverKey,
      partnerId: readNumber(location, 'partnerId') ?? currentDriver?.partnerId ?? null,
      driverId: locationDriver.driverId ?? currentDriver?.driverId ?? null,
      name: locationDriver.name || currentDriver?.name || '',
      mobile: locationDriver.mobile ?? currentDriver?.mobile ?? null,
      carManufacturer: locationDriver.carManufacturer ?? currentDriver?.carManufacturer ?? null,
      carNumber: locationDriver.carNumber ?? currentDriver?.carNumber ?? null,
      carColor: locationDriver.carColor ?? currentDriver?.carColor ?? null,
      status: readNumber(location, 'status') ?? currentDriver?.status ?? null,
      isOnline: locationDriver.isOnline,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      regionIds: currentDriver?.regionIds ?? [],
      tariffIds:
        locationDriver.tariffIds.length > 0 ? locationDriver.tariffIds : currentDriver?.tariffIds ?? [],
      activeTariffIds:
        locationDriver.activeTariffIds.length > 0
          ? locationDriver.activeTariffIds
          : currentDriver?.activeTariffIds ?? [],
      tariffAssignments:
        locationDriver.tariffAssignments.length > 0
          ? locationDriver.tariffAssignments
          : currentDriver?.tariffAssignments ?? [],
      paymentMethodIds:
        locationDriver.paymentMethodIds.length > 0
          ? locationDriver.paymentMethodIds
          : currentDriver?.paymentMethodIds ?? [],
      optionIds:
        locationDriver.optionIds.length > 0 ? locationDriver.optionIds : currentDriver?.optionIds ?? [],
      activeOptionIds:
        locationDriver.activeOptionIds.length > 0
          ? locationDriver.activeOptionIds
          : currentDriver?.activeOptionIds ?? [],
      optionAssignments:
        locationDriver.optionAssignments.length > 0
          ? locationDriver.optionAssignments
          : currentDriver?.optionAssignments ?? [],
      acceptanceProbability:
        locationDriver.acceptanceProbability !== 1
          ? locationDriver.acceptanceProbability
          : currentDriver?.acceptanceProbability ?? 1,
      lastOrderInterval: locationDriver.lastOrderInterval ?? currentDriver?.lastOrderInterval ?? null,
      lastAssignInterval: locationDriver.lastAssignInterval ?? currentDriver?.lastAssignInterval ?? null,
      ordersCount: locationDriver.ordersCount ?? currentDriver?.ordersCount ?? null,
      balance: locationDriver.balance ?? currentDriver?.balance ?? null,
      towardsStatus: readNumber(towards, 'status') ?? currentDriver?.towardsStatus ?? null,
      towardsAddress: readString(towards, 'address') ?? currentDriver?.towardsAddress ?? null,
      towardsStatusDesc: readString(towards, 'statusDesc') ?? currentDriver?.towardsStatusDesc ?? null,
      towardsInterval: readNumber(towards, 'interval') ?? currentDriver?.towardsInterval ?? null
    };
  }

  return {
    ...currentState,
    byId: nextById,
    ids: Object.keys(nextById),
    updatedAt: new Date().toISOString()
  };
}

export function getAllRegionIds(referenceDataState: ReferenceDataStateModel): number[] {
  return referenceDataState.regions.map((region) => region.id);
}

export function getAllCarTypeIds(referenceDataState: ReferenceDataStateModel): number[] {
  return [...new Set(referenceDataState.tariffs.map((tariff) => tariff.typeId).filter(isNumber))];
}

export function extractLegacyOrderRealtimeTariffId(rawOrder: unknown): number | null {
  const order = asRecord(parseSocketPayload(rawOrder));
  const carType = asRecord(order['carType']);

  return readNumber(carType, 'tariffId') ?? readNumber(order, 'tariff');
}

export function extractLegacyOrderSelectedTariffId(rawOrder: unknown): number | null {
  const order = asRecord(parseSocketPayload(rawOrder));
  const carType = asRecord(order['carType']);

  return readNumber(carType, 'tariffId') ?? readNumber(order, 'tariff');
}

export function matchesTrackingState(
  order: OrderRecord,
  trackingState: OrderTrackingState
): boolean {
  if (!trackingState.enabled || trackingState.tokens.length === 0) {
    return false;
  }

  // Tokens are typed or pasted by the operator, so accept the number as stored and as copied
  // (with the leading 0) for both the customer and the partner.
  const phoneForms = [
    ...localPhoneForms(normalizePhone(order.userMobile)),
    ...localPhoneForms(normalizePhone(order.driverMobile))
  ];

  if (phoneForms.some((form) => trackingState.tokens.includes(form))) {
    return true;
  }

  if (order.comment && trackingState.tokens.includes('comment')) {
    return true;
  }

  return trackingState.tokens.some((token) => order.address.includes(token));
}

export function normalizeOrderRecord(rawOrder: unknown): OrderRecord {
  const order = asRecord(rawOrder);
  const user = asRecord(order['user']);
  const driver = asRecord(order['driver']);
  const carType = asRecord(order['carType']);
  const destination = asRecord(order['destination']);
  const source = asRecord(order['source']);
  const car = asRecord(order['car']);

  return {
    orderId: readIdentifier(order, 'orderId') ?? '',
    statusId: readNumber(order, 'statusId'),
    userId: readIdentifier(order, 'userId') ?? readIdentifier(user, 'userId'),
    driverId: readIdentifier(driver, 'userId') ?? readIdentifier(order, 'driverId'),
    userFirstName: readString(user, 'firstName'),
    userLastName: readString(user, 'lastName'),
    driverFirstName: readString(driver, 'firstName'),
    driverLastName: readString(driver, 'lastName'),
    userMobile: normalizeNullablePhone(readString(user, 'mobile')),
    driverMobile: normalizeNullablePhone(readString(driver, 'mobile')),
    driverName: buildPersonName(driver),
    carManufacturer: readString(asRecord(car['manufacturer']), 'name'),
    carNumber: readString(car, 'number'),
    carColor: readString(car, 'color'),
    regionId: readNumber(order, 'regionId'),
    tariffId: extractLegacyOrderSelectedTariffId(order),
    paymentMethodId: readNumber(order, 'paymentMethodId'),
    acceptedDate: readString(order, 'acceptedDate'),
    cancelDate: readString(order, 'cancelDate'),
    completedDate: readString(order, 'completedDate'),
    createdDate: readString(order, 'createdDate') ?? readString(order, 'createdAt'),
    waitingDate: readString(order, 'waitingDate'),
    processingDate: readString(order, 'processingDate'),
    address: sanitizeLegacyText(readString(order, 'address') ?? ''),
    comment: sanitizeLegacyText(readString(order, 'comment') ?? ''),
    destinationAddress:
      sanitizeLegacyText(
        readString(destination, 'address') ?? readString(order, 'destinationAddress') ?? ''
      ),
    destinationComment:
      sanitizeLegacyText(
        readString(destination, 'comment') ?? readString(order, 'destinationComment') ?? ''
      ),
    destinationLatitude:
      readNumber(asRecord(destination['location']), 'lat') ?? readNumber(order, 'destinationLat'),
    destinationLongitude:
      readNumber(asRecord(destination['location']), 'lng') ?? readNumber(order, 'destinationLng'),
    latitude: readNumber(order, 'latitude'),
    longitude: readNumber(order, 'longitude'),
    carTypeId: readNumber(carType, 'typeId'),
    carTypeDescription:
      readString(carType, 'description') ?? readString(carType, 'name') ?? 'Unknown type',
    corporate: readBoolean(order, 'is_corporate'),
    business: readBoolean(order, 'is_business'),
    surgePrice: readNumber(order, 'surgePrice'),
    options: readArray(order, 'options')
      .map((option) => {
        if (typeof option === 'number') {
          return option;
        }

        return readNumber(asRecord(option), 'optionId') ?? readNumber(asRecord(option), 'id');
      })
      .filter(isNumber),
    processing: readBoolean(order, 'processing'),
    tracking: readBoolean(order, 'tracking'),
    orderDuplicate: readBoolean(order, 'orderDuplicate'),
    ordersInSixMonths: readNumber(order, 'ordersInSixMonths'),
    totalOrders: readNumber(order, 'totalOrders'),
    ordersInLastMonth: readNumber(order, 'ordersInLastMonth'),
    cancelCount: readNumber(order, 'cancelCount') ?? 0,
    fareEstimate: readNumber(order, 'fareEstimate'),
    hint: sanitizeLegacyText(readString(order, 'hint') ?? ''),
    merchantOrderId: readIdentifier(order, 'merchantOrderId'),
    preOrderId: readIdentifier(order, 'preOrderId'),
    preOrderPaymentTypeName: readString(order, 'preOrderPaymentTypeName'),
    deliveryDate: readString(order, 'deliveryDate'),
    businessDeliveryStatus: readNumber(order, 'status'),
    orderStops: normalizeOrderStops(readArray(order, 'orderStops')),
    sourcePlatform: readString(source, 'platform'),
    sourceAppVersion: readString(source, 'appVersion')
  };
}

function normalizeDriverRecord(rawPartner: unknown): DriverRecord {
  const partner = asRecord(rawPartner);
  const userInfo = asRecord(partner['userInfo']);
  const carInfo = asRecord(partner['carInfo']);
  const tariffs = readArray(partner, 'tariffs').map((entry) => asRecord(entry));
  const options = readArray(partner, 'options').map((entry) => asRecord(entry));
  const coordinates = parseLocationCoordinates(partner['location']);
  const driverKey = readIdentifier(partner, 'userId') ?? readIdentifier(partner, 'partnerId') ?? '';
  const topLevelTariffIds = readNumberArray(partner['tariffIds']);
  const topLevelActiveTariffIds = readNumberArray(partner['activeTariffIds']);
  const topLevelOptionIds = readNumberArray(partner['optionIds']);
  const topLevelActiveOptionIds = readNumberArray(partner['activeOptionIds']);
  const nestedTariffIds = tariffs.map((tariff) => readNumber(tariff, 'tariffId')).filter(isNumber);
  const nestedActiveTariffIds = tariffs
    .filter((tariff) => readBoolean(tariff, 'active'))
    .map((tariff) => readNumber(tariff, 'tariffId'))
    .filter(isNumber);
  const nestedOptionIds = options.map((option) => readNumber(option, 'optionId')).filter(isNumber);
  const nestedActiveOptionIds = options
    .filter((option) => readBoolean(option, 'active'))
    .map((option) => readNumber(option, 'optionId'))
    .filter(isNumber);

  return {
    userId: driverKey,
    partnerId: readNumber(partner, 'partnerId'),
    driverId: readString(partner, 'driverId'),
    name:
      buildPersonName(userInfo) ||
      buildPersonName(partner) ||
      '',
    mobile: normalizeNullablePhone(
      readString(userInfo, 'mobile') ?? readString(partner, 'mobile')
    ),
    carManufacturer:
      readString(asRecord(carInfo['manufacturer']), 'name') ?? readString(carInfo, 'manufacturer'),
    carNumber: readString(carInfo, 'number'),
    carColor: readString(carInfo, 'color'),
    status: readNumber(partner, 'status'),
    isOnline: partner['online'] !== false,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    regionIds: [],
    tariffIds: mergeUniqueNumbers(topLevelTariffIds, nestedTariffIds),
    activeTariffIds: mergeUniqueNumbers(topLevelActiveTariffIds, nestedActiveTariffIds),
    tariffAssignments: tariffs
      .map((tariff) => ({
        tariffId: readNumber(tariff, 'tariffId'),
        active: readBoolean(tariff, 'active'),
        activeChangeable: readBoolean(tariff, 'activeChangeable')
      }))
      .filter(
        (tariff): tariff is DriverRecord['tariffAssignments'][number] => isNumber(tariff.tariffId)
      ),
    paymentMethodIds: readArray(partner, 'paymentMethodIds')
      .map((paymentMethodId) => {
        if (typeof paymentMethodId === 'number') {
          return paymentMethodId;
        }

        if (typeof paymentMethodId === 'string' && paymentMethodId.trim() !== '') {
          return Number(paymentMethodId);
        }

        return readNumber(asRecord(paymentMethodId), 'paymentMethodId');
      })
      .filter(isNumber),
    optionIds: mergeUniqueNumbers(topLevelOptionIds, nestedOptionIds),
    activeOptionIds: mergeUniqueNumbers(topLevelActiveOptionIds, nestedActiveOptionIds),
    optionAssignments: options
      .map((option) => ({
        optionId: readNumber(option, 'optionId'),
        active: readBoolean(option, 'active'),
        activeChangeable: readBoolean(option, 'activeChangeable')
      }))
      .filter(
        (
          option
        ): option is {
          optionId: number;
          active: boolean;
          activeChangeable: boolean;
        } => isNumber(option.optionId)
      ),
    acceptanceProbability: readNumber(partner, 'acceptanceProbability') ?? 1,
    lastOrderInterval: readString(partner, 'last_order_interval'),
    lastAssignInterval: readString(partner, 'last_assign_interval'),
    ordersCount: readNumber(partner, 'orders_count'),
    balance: readNumber(userInfo, 'balance') ?? readNumber(partner, 'balance'),
    towardsStatus: readNumber(asRecord(partner['towards']), 'status'),
    towardsAddress: readString(asRecord(partner['towards']), 'address'),
    towardsStatusDesc: readString(asRecord(partner['towards']), 'statusDesc'),
    towardsInterval: readNumber(asRecord(partner['towards']), 'interval')
  };
}

function mergeDriverRecord(currentDriver: DriverRecord, nextDriver: DriverRecord): DriverRecord {
  return {
    ...currentDriver,
    ...nextDriver,
    partnerId: nextDriver.partnerId ?? currentDriver.partnerId,
    driverId: nextDriver.driverId ?? currentDriver.driverId,
    name: nextDriver.name || currentDriver.name,
    mobile: nextDriver.mobile ?? currentDriver.mobile,
    carManufacturer: nextDriver.carManufacturer ?? currentDriver.carManufacturer,
    carNumber: nextDriver.carNumber ?? currentDriver.carNumber,
    carColor: nextDriver.carColor ?? currentDriver.carColor,
    status: nextDriver.status ?? currentDriver.status,
    regionIds: nextDriver.regionIds.length > 0 ? nextDriver.regionIds : currentDriver.regionIds,
    tariffIds: nextDriver.tariffIds.length > 0 ? nextDriver.tariffIds : currentDriver.tariffIds,
    activeTariffIds:
      nextDriver.activeTariffIds.length > 0 ? nextDriver.activeTariffIds : currentDriver.activeTariffIds,
    tariffAssignments:
      nextDriver.tariffAssignments.length > 0
        ? nextDriver.tariffAssignments
        : currentDriver.tariffAssignments,
    paymentMethodIds:
      nextDriver.paymentMethodIds.length > 0 ? nextDriver.paymentMethodIds : currentDriver.paymentMethodIds,
    optionIds: nextDriver.optionIds.length > 0 ? nextDriver.optionIds : currentDriver.optionIds,
    activeOptionIds:
      nextDriver.activeOptionIds.length > 0 ? nextDriver.activeOptionIds : currentDriver.activeOptionIds,
    optionAssignments:
      nextDriver.optionAssignments.length > 0
        ? nextDriver.optionAssignments
        : currentDriver.optionAssignments,
    lastOrderInterval: nextDriver.lastOrderInterval ?? currentDriver.lastOrderInterval,
    lastAssignInterval: nextDriver.lastAssignInterval ?? currentDriver.lastAssignInterval,
    ordersCount: nextDriver.ordersCount ?? currentDriver.ordersCount,
    balance: nextDriver.balance ?? currentDriver.balance,
    towardsStatus: nextDriver.towardsStatus ?? currentDriver.towardsStatus,
    towardsAddress: nextDriver.towardsAddress ?? currentDriver.towardsAddress,
    towardsStatusDesc: nextDriver.towardsStatusDesc ?? currentDriver.towardsStatusDesc,
    towardsInterval: nextDriver.towardsInterval ?? currentDriver.towardsInterval
  };
}

function normalizeOnlineUserRecord(rawUser: unknown, index: number): OnlineUserRecord {
  const user = asRecord(rawUser);
  const coordinates = parseLocationCoordinates(user['location'] ?? user['coordinates'] ?? rawUser);

  return {
    id:
      readIdentifier(user, 'user_id') ??
      readIdentifier(user, 'userId') ??
      readIdentifier(user, 'id') ??
      `${index}`,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    raw: user
  };
}

function markDuplicateOrders(state: OrdersStateModel, userId: string | null): OrdersStateModel {
  if (!userId) {
    return state;
  }

  const duplicateIds = state.allIds.filter((orderId) => state.byId[orderId]?.userId === userId);

  if (duplicateIds.length < 2) {
    return state;
  }

  const nextById = { ...state.byId };

  for (const orderId of duplicateIds) {
    nextById[orderId] = {
      ...nextById[orderId],
      orderDuplicate: true
    };
  }

  return {
    ...state,
    byId: nextById
  };
}

function normalizeOrderStops(rawStops: unknown[]): OrderStopRecord[] {
  return rawStops
    .map((rawStop, index) => {
      const stop = asRecord(rawStop);
      const stopId = readNumber(stop, 'id');
      const seq = readNumber(stop, 'seq');

      return {
        id: stopId ?? index,
        stopType: readNumber(stop, 'stopType') ?? 1,
        tripId: readNumber(stop, 'tripId') ?? 0,
        address: sanitizeLegacyText(readString(stop, 'address') ?? ''),
        comment: sanitizeLegacyText(readString(stop, 'comment') ?? ''),
        latitude: readNumber(stop, 'latitude'),
        longitude: readNumber(stop, 'longitude'),
        status: readBoolean(stop, 'status'),
        seq: seq ?? index
      };
    })
    .sort((left, right) => left.seq - right.seq);
}

function applyTrackingState(order: OrderRecord, trackingState: OrderTrackingState): OrderRecord {
  return {
    ...order,
    tracking: matchesTrackingState(order, trackingState)
  };
}

function appendUnique(items: string[], value: string): string[] {
  return items.includes(value) ? items : [...items, value];
}

function parseSocketPayload(payload: unknown): unknown {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }

  return payload;
}

function sanitizeLegacyText(value: string): string {
  return value
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
    .replace(/\f/g, '\\f')
    .trim();
}

function normalizePhone(value: string | null): string {
  if (!value) {
    return '';
  }

  return value.replace('+374', '').replace('+995', '');
}

function normalizeNullablePhone(value: string | null): string | null {
  return value ? normalizePhone(value) : null;
}

function parseLocationCoordinates(location: unknown): {
  latitude: number | null;
  longitude: number | null;
} {
  if (Array.isArray(location) && location.length >= 2) {
    const longitude = Number(location[0]);
    const latitude = Number(location[1]);

    return {
      latitude: Number.isFinite(latitude) ? latitude : null,
      longitude: Number.isFinite(longitude) ? longitude : null
    };
  }

  const record = asRecord(location);
  const latitude = readNumber(record, 'lat') ?? readNumber(record, 'latitude');
  const longitude = readNumber(record, 'lng') ?? readNumber(record, 'longitude');

  return { latitude, longitude };
}

function normalizeCoordinates(rawCoordinates: unknown[]): Array<[number, number]> {
  return rawCoordinates
    .map((coordinateEntry) => {
      if (!Array.isArray(coordinateEntry) || coordinateEntry.length < 2) {
        return null;
      }

      const longitude = readNumber({ longitude: coordinateEntry[0] }, 'longitude');
      const latitude = readNumber({ latitude: coordinateEntry[1] }, 'latitude');

      if (!isNumber(longitude) || !isNumber(latitude)) {
        return null;
      }

      return [longitude, latitude] as [number, number];
    })
    .filter((coordinate): coordinate is [number, number] => coordinate !== null);
}

function readNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (typeof entry === 'number') {
        return entry;
      }

      if (typeof entry === 'string' && entry.trim() !== '') {
        return Number(entry);
      }

      return null;
    })
    .filter((entry): entry is number => isNumber(entry));
}

function mergeUniqueNumbers(...groups: number[][]): number[] {
  return [...new Set(groups.flat().filter(isNumber))];
}

function asRecord(value: unknown): UnknownRecord {
  return typeof value === 'object' && value !== null ? (value as UnknownRecord) : {};
}

function readJsonRecord(value: unknown): UnknownRecord {
  if (typeof value === 'object' && value !== null) {
    return value as UnknownRecord;
  }

  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      return asRecord(parsed);
    } catch {
      return {};
    }
  }

  return {};
}

function readString(record: UnknownRecord, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readIdentifier(record: UnknownRecord, key: string): string | null {
  const stringValue = readString(record, key);

  if (stringValue) {
    return stringValue;
  }

  const numericValue = readNumber(record, key);
  return numericValue !== null ? `${numericValue}` : null;
}

function readNumber(record: UnknownRecord, key: string): number | null {
  const value = record[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
}

function readBoolean(record: UnknownRecord, key: string): boolean {
  const value = record[key];
  return value === true || value === 'true' || value === 1 || value === '1';
}

function readArray(record: UnknownRecord, key: string): unknown[] {
  const value = record[key];
  return Array.isArray(value) ? value : [];
}

function buildPersonName(record: UnknownRecord): string {
  const fullName = readString(record, 'fullName') ?? readString(record, 'name');

  if (fullName) {
    return fullName;
  }

  const firstName = readString(record, 'firstName') ?? readString(record, 'firstname') ?? '';
  const lastName = readString(record, 'lastName') ?? readString(record, 'lastname') ?? '';
  return `${firstName} ${lastName}`.trim();
}

function isNumber(value: number | null): value is number {
  return value !== null;
}
